import React from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const SkillConceptModal = ({ concept, conversationId, onClose }) => {
  const navigate = useNavigate();

  // Add custom scrollbar styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #e5e7eb;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #d1d5db;
      }
      .dark .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #374151;
      }
      .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #4b5563;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleBuildSkill = () => {
    if (!concept) {
      console.error('No concept data');
      return;
    }

    // Combine all context into a comprehensive description
    let fullDescription = concept.description || '';
    
    // Add Primary Function if available
    if (concept.primaryFunction) {
      fullDescription += `\n\nPrimary Function: ${concept.primaryFunction}`;
    }
    
    // Add Ideal For if available
    if (concept.idealFor) {
      fullDescription += `\n\nIdeal For: ${concept.idealFor}`;
    }

    // Store data with the comprehensive description
    const data = {
      name: concept.skillName || '',
      description: fullDescription.trim(),
      instructions: concept.primaryFunction || concept.purpose || '',
      primaryFunction: concept.primaryFunction || '',
      idealFor: concept.idealFor || '',
      purpose: concept.purpose || '',
      keyCapabilities: concept.keyCapabilities || [],
      category: concept.category || ''
    };

    localStorage.setItem('DISCOVERY_DATA', JSON.stringify(data));
    console.log('✅ Discovery data stored:', data);

    // Navigate to wizard - create new skill
    navigate('/builder/new/step-1?from=discovery');
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" style={{ zIndex: 10000 }} onClick={onClose}>
      <div 
        className="bg-white dark:bg-dark-bg-secondary rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl border dark:border-[#2e2e2d] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-dark-bg-secondary border-b dark:border-[#2e2e2d] px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Your Skill Concept is Ready!</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Review the details and let's build it</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div 
          className="p-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#e5e7eb transparent'
          }}
        >
          {/* Skill Name and Category */}
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-bg-tertiary">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
                  Skill Name
                </label>
                <p className="text-2xl font-bold">{concept.skillName || 'Untitled Skill'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
                  Category
                </label>
                <Badge className="bg-primary/10 text-primary border-primary/30 text-sm mt-1">
                  {concept.category || 'General'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-bg-tertiary">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
              Description
            </label>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {concept.description || 'No description provided'}
            </p>
          </div>

          {/* Primary Function */}
          {concept.primaryFunction && (
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-bg-tertiary">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
                Primary Function
              </label>
              <p className="text-gray-700 dark:text-gray-300">
                {concept.primaryFunction}
              </p>
            </div>
          )}

          {/* Ideal For - Full Width */}
          {concept.idealFor && (
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-bg-tertiary">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
                Ideal For
              </label>
              <p className="text-gray-700 dark:text-gray-300">
                {concept.idealFor}
              </p>
            </div>
          )}

          {/* Key Capabilities - Single Column */}
          {concept.keyCapabilities && concept.keyCapabilities.length > 0 && (
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-bg-tertiary">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 block">
                Key Capabilities
              </label>
              <ul className="space-y-2">
                {concept.keyCapabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span className="text-gray-700 dark:text-gray-300">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-dark-bg-tertiary border-t dark:border-[#2e2e2d] px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="px-4 py-2 font-medium"
          >
            Refine More
          </Button>
          <Button
            onClick={handleBuildSkill}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            Build This Skill
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default SkillConceptModal;
